import React from "react";
import { FaUserFriends, FaChalkboardTeacher, FaSchool, FaSearch, FaBrain, FaHandshake, FaChartLine, FaStar, FaPuzzlePiece, FaUserMd, FaCalendarAlt, FaGlobe } from "react-icons/fa";
import useAuthUser from '../hooks/useAuthUser';
import useUserRole from '../hooks/useUserRole';
import { normalizeRole } from '../utils/roleUtils';

const features = [
  {
    icon: <FaUserFriends className="text-6xl text-indigo-400 drop-shadow-lg mx-auto" />,
    title: "Parents",
    bg: "bg-gradient-to-br from-green-100 via-green-50 to-white",
    titleColor: "text-indigo-900 group-hover:text-indigo-700",
    textColor: "text-indigo-800",
    cta: "Join as Parent",
    ctaLink: "/signup?role=parent",
    button: "bg-indigo-600 hover:bg-indigo-700",
    features: [
      "🔎 Find a shadow teacher for school integration",
      "🧠 Discover expert therapists – Occupational (OT), Behavioral (BT), Speech (ST)",
      "🤝 Personalized matches based on your child’s unique needs",
      "📈 Easy onboarding and real-time progress tracking"
    ]
  },
  {
    icon: <FaChalkboardTeacher className="text-6xl text-pink-400 drop-shadow-lg mx-auto" />,
    title: "Grow your practice with meaningful impact",
    bg: "bg-gradient-to-br from-pink-100 via-pink-50 to-white",
    titleColor: "text-pink-900 group-hover:text-pink-700",
    textColor: "text-pink-800",
    cta: "Join as Therapist",
    ctaLink: "/signup?role=therapist",
    button: "bg-pink-500 hover:bg-pink-600",
    features: [
      { icon: <FaHandshake />, text: "Connect with families and inclusive schools" },
      { icon: <FaStar />, text: "Showcase your specialties and certifications" },
      { icon: <FaPuzzlePiece />, text: "Explore flexible work opportunities across locations" }
    ]
  },
  {
    icon: <FaSchool className="text-6xl text-yellow-400 drop-shadow-lg mx-auto" />,
    title: "Build an inclusive support system at your CDC/school",
    bg: "bg-gradient-to-br from-blue-100 via-blue-50 to-white",
    titleColor: "text-blue-900 group-hover:text-blue-700",
    textColor: "text-blue-800",
    cta: "List Your Center",
    ctaLink: "/signup?role=cdc",
    button: "bg-blue-600 hover:bg-blue-700",
    features: [
      { icon: <FaUserMd />, text: "Access a verified pool of therapists and shadow teachers" },
      { icon: <FaCalendarAlt />, text: "Simplify hiring, onboarding, and collaboration" },
      { icon: <FaGlobe />, text: "Enable inclusive education through structured support" }
    ]
  }
];

export default function PlatformFeatures({ user: injectedUser = null, role: injectedRole = null, onOpenSignup }) {
  // If user/role are passed as props, use them; else use hooks
  const user = injectedUser !== null ? injectedUser : useAuthUser();
  const userRole = injectedRole !== null ? injectedRole : useUserRole(user);

  // Use the utility function for role normalization
  const getNormalizedRole = () => {
    if (!userRole) return '';
    return normalizeRole(userRole.role || userRole, user?.email);
  };

  // Card configs
  const cards = [
    {
      key: 'parent',
      render: () => (
        <div className="flex-1 min-w-[300px] max-w-sm mx-auto feature-card relative rounded-2xl p-8 flex flex-col items-center bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group hover:-translate-y-1">
          {user && normalizeRole(userRole) === 'parent' && (
            <span className="absolute top-3 right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">Your Features</span>
          )}

          <div className="w-full mb-6 p-4 bg-gradient-to-r from-indigo-100 to-indigo-200 rounded-xl group-hover:from-indigo-200 group-hover:to-indigo-300 transition-all duration-300 shadow-sm">
            <h3 className="text-2xl font-bold text-indigo-900 flex items-center gap-4">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-lg text-3xl">👪</span>
              <span className="bg-gradient-to-r from-indigo-700 to-indigo-900 bg-clip-text text-transparent">I am a Parent</span>
            </h3>
          </div>
          <ul className="text-gray-700 space-y-3 mb-6 text-base font-medium text-left w-full">
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-indigo-500">🩺</span>Take the free M-CHAT autism screening</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-indigo-500">👩‍⚕️</span>Find therapists & shadow teachers nearby</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-indigo-500">🤖</span>Get matched using AI for your child's needs</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-indigo-500">📈</span>Track progress with structured support</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-indigo-500">💡</span>Get expert help in one place</li>
          </ul>
          <div className="w-full">
            <a
              href="/free-assessment"
              className="w-full inline-block mt-2 px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold shadow-md transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 text-sm text-center transform hover:-translate-y-0.5"
            >Take Free Assessment →</a>
          </div>
        </div>
      )
    },
    {
      key: 'professional',
      render: () => (
        <div className="flex-1 min-w-[300px] max-w-sm mx-auto feature-card relative rounded-2xl p-8 flex flex-col items-center bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group hover:-translate-y-1">
          {user && normalizeRole(userRole) === 'professional' && (
            <span className="absolute top-3 right-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">Your Features</span>
          )}

          <div className="w-full mb-6 p-4 bg-gradient-to-r from-pink-100 to-pink-200 rounded-xl group-hover:from-pink-200 group-hover:to-pink-300 transition-all duration-300 shadow-sm">
            <h3 className="text-2xl font-bold text-pink-900 flex items-center gap-4">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-lg text-3xl">👩‍⚕️</span>
              <span className="bg-gradient-to-r from-pink-700 to-pink-900 bg-clip-text text-transparent">I am a Professional</span>
            </h3>
          </div>
          <ul className="text-gray-700 space-y-3 mb-6 text-base font-medium text-left w-full">
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-pink-500">🤖</span>Get AI-matched with nearby families & centers</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-pink-500">⭐</span>Showcase skills, certifications & availability</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-pink-500">🕒</span>Flexible jobs for therapists, teachers & psychologists</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-pink-500">🤝</span>Collaborate easily and grow your network</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-pink-500">💼</span>Find jobs faster, closer to home</li>
          </ul>
          {!user && (
            <button
              type="button"
              className="w-full mt-2 px-6 py-3 rounded-lg bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-semibold shadow-md transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 transform hover:-translate-y-0.5"
              onClick={() => onOpenSignup && onOpenSignup('therapist')}
            >
              Find Jobs →
            </button>
          )}
        </div>
      )
    },
    {
      key: 'organization',
      render: () => (
        <div className="flex-1 min-w-[300px] max-w-sm mx-auto feature-card relative rounded-2xl p-8 flex flex-col items-center bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group hover:-translate-y-1">
          {user && getNormalizedRole() === 'organization' && (
            <span className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">Your Features</span>
          )}

          <div className="w-full mb-6 p-4 bg-gradient-to-r from-amber-100 to-amber-200 rounded-xl group-hover:from-amber-200 group-hover:to-amber-300 transition-all duration-300 shadow-sm">
            <h3 className="text-2xl font-bold text-amber-900 flex items-center gap-4">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-lg text-3xl">🏫</span>
              <span className="bg-gradient-to-r from-amber-700 to-amber-900 bg-clip-text text-transparent">I am a CDC/School</span>
            </h3>
          </div>
          <ul className="text-gray-700 space-y-3 mb-6 text-base font-medium text-left w-full">
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-amber-500">✅</span>Verified therapists & shadow teachers</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-amber-500">🤖</span>AI matching for faster hiring</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-amber-500">📝</span>Simplify onboarding & management</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-amber-500">🌈</span>Build an inclusive setup with ease</li>
            <li className="flex items-start gap-3 py-1.5"><span className="text-xl text-amber-500">👥</span>Hire your full team in one place</li>
          </ul>
          {!user && (
            <button
              type="button"
              className="w-full mt-2 px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold shadow-md transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 transform hover:-translate-y-0.5"
              onClick={() => onOpenSignup && onOpenSignup('cdc')}
            >
              Post Job Free →
            </button>
          )}
        </div>
      )
    }
  ];

  // Navigation function
  const navigateToDashboard = (card) => {
    if (!user) return;
    const role = getNormalizedRole();
    let path = '/';
    
    switch(role) {
      case 'parent':
        path = '/app/dashboard/parent';
        break;
      case 'professional':
        path = '/app/dashboard/professional';
        break;
      case 'organization':
        path = '/app/dashboard/organization';
        break;
      default:
        break;
    }
    
    if (path !== '/') {
      window.location.href = path;
    }
  };

  // Render logic
  let visibleCards = cards;
  if (user && userRole) {
    const normRole = getNormalizedRole();
    if (['parent', 'professional', 'organization'].includes(normRole)) {
      visibleCards = cards.filter(card => card.key === normRole);
    }
  }

  return (
    <section id="features" className="py-20 px-4 relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-50 via-white/90 to-white/95">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%230ea5e9' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-16 px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {user ? 'Welcome to Your Dashboard' : 'Made for Parents, Professionals & Centers'}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 mx-auto rounded-full"></div>
        </div>
        
        <div className={`grid ${user ? 'grid-cols-1 max-w-2xl' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-8 mx-auto px-4`}>
          {visibleCards.map((card, index) => (
            <div 
              key={index} 
              onClick={() => user && navigateToDashboard(card)}
              className={`h-full transition-all duration-300 ${user ? 'w-full cursor-pointer group' : ''} hover:-translate-y-1`}
            >
              <div className={`h-full ${user ? 'group-hover:ring-2 group-hover:ring-blue-500 group-hover:ring-opacity-50 rounded-2xl transition-all duration-200' : ''}`}>
                {card.render()}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Decorative elements with subtle animations */}
      <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float2"></div>
      <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-amber-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float3"></div>
    </section>
  );
}
